import {
    ShaderMaterial, CylinderGeometry,
    Vector3, Group, Mesh, MathUtils,
    LinearFilter, DoubleSide, RepeatWrapping,
} from "three";

import Color from './Color.js';

export default class Godray extends Group {
    constructor(settings) {
        super();

        this.settings = {
            position: [0, 0, 0],
            rotation: [0, 0, 0],
            radiusTop: 10,
            radiusBottom: 3,
            height: 50,
            radialSegments: 64,
            color: "white",
            noiseScale: 5,
            noiseRepeat: 1.0,
            noiseContrast: 1.0,
            timeSpeed: 0.5,
            smoothTop: 1,
            smoothBottom: 0.1,
            fresnelPower: 5,
            ...settings,
        };

        const geometry = new CylinderGeometry(
            this.settings.radiusTop,
            this.settings.radiusBottom,
            this.settings.height,
            this.settings.radialSegments,
            1,
            true
        );

        const noiseTexture = this.settings.texture;

        noiseTexture.wrapS = RepeatWrapping;
        noiseTexture.wrapT = RepeatWrapping;
        noiseTexture.minFilter = LinearFilter;
        noiseTexture.magFilter = LinearFilter;

        this.uniforms = {
            uTime: { value: 0 },
            uCameraPosition: { value: new Vector3() },
            uColor: { value: new Color( this.settings.color ) },
            uNoiseScale: { value: this.settings.noiseScale },
            uNoiseRepeat: { value: this.settings.noiseRepeat },
            uNoiseContrast: { value: this.settings.noiseContrast },
            uTimeSpeed: { value: this.settings.timeSpeed },
            uSmoothTop: { value: this.settings.smoothTop },
            uSmoothBottom: { value: this.settings.smoothBottom },
            uFresnelPower: { value: this.settings.fresnelPower },
            uNoiseTexture: { value: noiseTexture },
        };

        this.material = new ShaderMaterial({
            uniforms: this.uniforms,
            vertexShader: `
                varying vec2 vUv;
                varying vec3 vNormal;
                varying vec3 vWorldPosition;
                void main() {
                    vUv = uv;
                    vNormal = normalize(normalMatrix * normal);
                    vWorldPosition = (modelMatrix * vec4(position, 1.0)).xyz;
                    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
                }
            `,
            fragmentShader: `
                uniform vec3 uColor;
                uniform float uNoiseScale;
                uniform float uNoiseRepeat;
                uniform float uNoiseContrast;
                uniform float uTime;
                uniform float uTimeSpeed;
                uniform float uSmoothTop;
                uniform float uSmoothBottom;
                uniform float uFresnelPower;
                uniform sampler2D uNoiseTexture;
                uniform vec3 uCameraPosition;

                varying vec2 vUv;
                varying vec3 vNormal;
                varying vec3 vWorldPosition;

                void main() {
                    // UV animés avec le bruit
                    vec2 customUV = vNormal.xy * uNoiseScale + (uTime * uTimeSpeed);
                    vec2 noiseUV = fract(customUV * uNoiseRepeat);

                    float noiseValue = texture2D(uNoiseTexture, noiseUV).r;
                    noiseValue = pow(noiseValue, uNoiseContrast);
                    // Applique un léger flou (optionnel)
                    noiseValue = smoothstep(0.2, 0.8, noiseValue); // Réduit les contrastes brutaux

                    // Lissage vertical (top/bottom)
                    float smoothFactor = smoothstep(0.0, uSmoothBottom, vUv.y) *
                                        (1.0 - smoothstep(uSmoothTop, 1.0, vUv.y));

                    // Effet Fresnel
                    vec3 viewDir = normalize(uCameraPosition - vWorldPosition);
                    float fresnelFactor = dot(normalize(vNormal), viewDir);
                    float invertedFresnel = pow(abs(fresnelFactor), uFresnelPower);

                    // Alpha final
                    float finalAlpha = noiseValue * invertedFresnel * smoothFactor;

                    gl_FragColor = vec4(uColor, finalAlpha);
                }
            `,
            transparent: true,
            side: DoubleSide,
        });

        // this.material = new MeshBasicMaterial({
        //     color: new Color("yellow"),
        //     transparent: true,
        //     opacity: 0.01,
        //     side: DoubleSide,
        // });

        this.mesh = new Mesh( geometry, this.material );
        this.updatePosition( ...this.settings.position );
        this.updateRotation(...this.settings.rotation );
        this.add( this.mesh );
    }

    update(delta) {
        this.uniforms.uTime.value += delta * 0.15;
    }

    withControls(controls) {
        if ( controls ) {
            const dataGeometry = {
                radiusTop: this.settings.radiusTop,
                radiusBottom: this.settings.radiusBottom,
                height: this.settings.height,
                radialSegments: this.settings.radialSegments,
            };

            const dataPosition = {
                x: this.settings.position[0],
                y: this.settings.position[1],
                z: this.settings.position[2],
            };

            const dataRotation = {
                x: this.settings.rotation[0],
                y: this.settings.rotation[1],
                z: this.settings.rotation[2],
            };

            const createGeometry = () => this.mesh.geometry = this.createGeometry( dataGeometry );

            const updateMesh = () => {
                this.updatePosition( ...[ dataPosition.x, dataPosition.y, dataPosition.z ] );
                this.updateRotation( ...[ dataRotation.x, dataRotation.y, dataRotation.z ] );
            };

            const folderGeometry = controls.folder( 'THREE.CylinderGeometry' );

            folderGeometry.add( dataGeometry, 'radiusTop', 1, 100 ).step( 1 ).listen().onChange( createGeometry );
            folderGeometry.add( dataGeometry, 'radiusBottom', 1, 100 ).step( 1 ).listen().onChange( createGeometry );
            folderGeometry.add( dataGeometry, 'height', 1, 150 ).step( 1 ).listen().onChange( createGeometry );
            folderGeometry.add( dataGeometry, 'radialSegments', 3, 64 ).step( 1 ).listen().onChange( createGeometry );

            const folderPositionMesh = controls.folder( 'THREE.mesh.position' );

            folderPositionMesh.add( dataPosition, "x", -100, 100 ).step( 1 ).listen().onChange( updateMesh );
            folderPositionMesh.add( dataPosition, "y", -100, 100 ).step( 1 ).listen().onChange( updateMesh );
            folderPositionMesh.add( dataPosition, "z", -100, 100 ).step( 1 ).listen().onChange( updateMesh );

            const folderRotationMesh = controls.folder( 'THREE.mesh.rotation' );

            folderRotationMesh.add( dataRotation, "x", 0, 360 ).step( 1 ).listen().onChange( updateMesh );
            folderRotationMesh.add( dataRotation, "y", 0, 360 ).step( 1 ).listen().onChange( updateMesh );
            folderRotationMesh.add( dataRotation, "z", 0, 360 ).step( 1 ).listen().onChange( updateMesh );
        }
    }

    createGeometry(data) {
        return new CylinderGeometry(
            data.radiusTop,
            data.radiusBottom,
            data.height,
            data.radialSegments,
            1,
            false
        )
    }

    updatePosition(x, y, z) {
        this.mesh.position.set( x, y, z );
    }

    updateRotation(x, y, z) {
        this.mesh.rotation.set(
            MathUtils.degToRad( x ),
            MathUtils.degToRad( y ),
            MathUtils.degToRad( z )
        );
    }
}